<template>
  <div v-if="totalItems > pageSize" class="pagination">
    <button 
      @click="goToFirstPage" 
      :disabled="currentPage === 1"
      class="pagination-btn"
      :aria-label="$t('common.pagination.first')">
      <i class="fas fa-angle-double-left"></i>
    </button>
    <button 
      @click="goToPreviousPage" 
      :disabled="currentPage === 1"
      class="pagination-btn"
      :aria-label="$t('common.pagination.previous')">
      <i class="fas fa-angle-left"></i>
    </button>
    
    <div class="pagination-info">
      <span class="page-numbers">
        {{ $t('common.pagination.page') }} {{ currentPage }} {{ $t('common.pagination.of') }} {{ totalPages }}
      </span>
      <span class="total-items">
        ({{ totalItems }} {{ itemsLabel }})
      </span>
    </div>
    
    <button 
      @click="goToNextPage" 
      :disabled="currentPage === totalPages"
      class="pagination-btn"
      :aria-label="$t('common.pagination.next')">
      <i class="fas fa-angle-right"></i>
    </button>
    <button 
      @click="goToLastPage" 
      :disabled="currentPage === totalPages"
      class="pagination-btn"
      :aria-label="$t('common.pagination.last')">
      <i class="fas fa-angle-double-right"></i>
    </button>
  </div>
</template>

<script setup lang="ts">
// @ts-ignore
import { computed } from 'vue';

interface Props {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  itemsLabel?: string;
}

interface Emits {
  (e: 'update:currentPage', value: number): void;
}

const props = withDefaults(defineProps<Props>(), {
  itemsLabel: 'items'
});

const emit = defineEmits<Emits>();

const totalPages = computed(() => {
  return Math.ceil(props.totalItems / props.pageSize);
});

const goToFirstPage = () => {
  if (props.currentPage !== 1) {
    emit('update:currentPage', 1);
  }
};

const goToPreviousPage = () => {
  if (props.currentPage > 1) {
    emit('update:currentPage', props.currentPage - 1);
  }
};

const goToNextPage = () => {
  if (props.currentPage < totalPages.value) {
    emit('update:currentPage', props.currentPage + 1);
  }
};

const goToLastPage = () => {
  if (props.currentPage !== totalPages.value) {
    emit('update:currentPage', totalPages.value);
  }
};
</script>

<style scoped>
/* Pagination Styles */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

.pagination-btn {
  width: 40px;
  height: 40px;
  border: 1px solid #e5e5e5;
  background: white;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #525252;
  font-size: 1rem;
}

.pagination-btn:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #6366f1;
  color: #6366f1;
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  margin: 0 1rem;
}

.page-numbers {
  font-weight: 600;
  color: #171717;
  font-size: 0.875rem;
}

.total-items {
  font-size: 0.75rem;
  color: #737373;
}

@media (max-width: 768px) {
  .pagination {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .pagination-info {
    order: -1;
    width: 100%;
    justify-content: center;
  }
}
</style>
